import {
  computed,
  getCurrentInstance,
  getCurrentScope,
  inject,
  onScopeDispose,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue';
import { matchedRouteKey, onBeforeRouteLeave } from 'vue-router';
import { useNxConfirm } from '../Confirm/useNxConfirm';

export interface NxUnsavedChangesLabels {
  /** Dialog title. Defaults to „Ungespeicherte Änderungen“. */
  title?: string;
  /** Confirm action with `save`. Defaults to „Speichern“. */
  save?: string;
  /** The way out that drops the changes. Defaults to „Nicht speichern“ (with `save`) or „Verwerfen“. */
  discard?: string;
  /** Defaults to „Abbrechen“. */
  cancel?: string;
}

export interface UseUnsavedChangesGuardOptions {
  /**
   * Saves the changes. With it the dialog offers Speichern / Nicht speichern /
   * Abbrechen; without it only Verwerfen / Abbrechen. Resolving `false` or
   * throwing keeps the user where they are — the save failed, leaving would
   * lose the work.
   */
  save?: () => Promise<boolean | void> | boolean | void;
  /** Called when the user chose to leave without saving (drop a draft, reset a store). */
  discard?: () => void;
  /** Body text of the dialog. */
  message?: string;
  labels?: NxUnsavedChangesLabels;
  /** Ask the browser to hold a reload / tab close while dirty. Defaults to `true`. */
  beforeUnload?: boolean;
  /**
   * Guard in-app route leaves with `onBeforeRouteLeave`. Defaults to `true`;
   * only takes effect when the caller sits inside a `<RouterView>`.
   */
  routeLeave?: boolean;
}

export interface UnsavedChangesGuard {
  /** The dirty flag as the guard sees it. */
  isDirty: ComputedRef<boolean>;
  /**
   * Asks when there are unsaved changes and resolves whether the caller may go
   * on — for leaves the router does not see (closing a dialog, switching the
   * record in a master-detail list). Resolves `true` at once when clean.
   * Concurrent calls share one dialog.
   */
  confirmLeave: () => Promise<boolean>;
}

const DEFAULT_MESSAGE =
  'Es gibt ungespeicherte Änderungen. Möchtest du sie speichern, bevor du gehst?';
const DEFAULT_MESSAGE_NO_SAVE =
  'Es gibt ungespeicherte Änderungen. Beim Verlassen gehen sie verloren.';

/**
 * One guard for „leave with unsaved changes?“: the browser's own prompt on
 * reload / tab close, a router guard on in-app navigation, and
 * `confirmLeave()` for everything else. The question goes through
 * `useNxConfirm`, so it renders in the kit's dialog when the shell mounted
 * `NxConfirmHost` and falls back to the browser's `confirm()` otherwise (there
 * OK means „save“ when `save` is given).
 *
 * ```ts
 * const { confirmLeave } = useUnsavedChangesGuard(() => doc.dirty, { save: () => doc.save() });
 * async function close() { if (await confirmLeave()) open.value = false; }
 * ```
 *
 * Call it in `setup()` (or an effect scope); the listeners go away with the scope.
 */
export function useUnsavedChangesGuard(
  isDirty: MaybeRefOrGetter<boolean>,
  options: UseUnsavedChangesGuardOptions = {}
): UnsavedChangesGuard {
  const confirm = useNxConfirm();
  const dirty = computed(() => Boolean(toValue(isDirty)));
  let pending: Promise<boolean> | null = null;

  async function ask(): Promise<boolean> {
    const labels = options.labels ?? {};
    const title = labels.title ?? 'Ungespeicherte Änderungen';
    const cancelLabel = labels.cancel ?? 'Abbrechen';

    if (!options.save) {
      const leave = await confirm({
        title,
        message: options.message ?? DEFAULT_MESSAGE_NO_SAVE,
        confirmLabel: labels.discard ?? 'Verwerfen',
        cancelLabel,
        tone: 'danger',
      });
      if (leave) options.discard?.();
      return leave;
    }

    const choice = await confirm({
      title,
      message: options.message ?? DEFAULT_MESSAGE,
      confirmLabel: labels.save ?? 'Speichern',
      alternativeLabel: labels.discard ?? 'Nicht speichern',
      cancelLabel,
    });
    if (choice === 'cancel') return false;
    if (choice === 'alternative') {
      options.discard?.();
      return true;
    }
    try {
      return (await options.save()) !== false;
    } catch {
      return false;
    }
  }

  function confirmLeave(): Promise<boolean> {
    if (!dirty.value) return Promise.resolve(true);
    pending ??= ask().finally(() => {
      pending = null;
    });
    return pending;
  }

  if (options.beforeUnload !== false && typeof window !== 'undefined') {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty.value) return;
      event.preventDefault();
      // Chrome still wants returnValue set; the text is ignored everywhere.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    if (getCurrentScope()) {
      onScopeDispose(() => window.removeEventListener('beforeunload', onBeforeUnload));
    }
  }

  // `onBeforeRouteLeave` warns outside a routed component, so look for the
  // route record the RouterView provides first.
  if (options.routeLeave !== false && getCurrentInstance()) {
    const matched = inject(matchedRouteKey, null);
    if (matched?.value) onBeforeRouteLeave(() => confirmLeave());
  }

  return { isDirty: dirty, confirmLeave };
}
